import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useMutation } from '@tanstack/react-query';
import { getCurrencyCodeFromCountry } from '@utils/getCurrencySymbol';
import { useStore } from '@hooks/useStore';
import { useMyTranslations } from '@hooks/useMyTranslations';
import { useGetBrandData } from '@hooks/useGetBrandData';
import { PageHeader } from '@components/Core/PageHeader';
import BrandTabs from '@components/Core/BrandTabs';
import { AppLoader } from '@components/Core/AppLoader';
import { AddEntityButton } from '@components/Core/AddEntityButton';
import Popup from '@components/Core/Popup/Popup';
import ProductsTable from '@tables/ProductsTable';
import { UploadCSVPopup } from '@popups/UploadCSVPopup';
import { Input } from '@inputs/Input';
import ImportIcon from '@icons/import_icon.svg?component';
import { ProductUploadCSVForm } from '@stores/products/types';
import { productsTranslations } from './translations';
import { ProductsTransFields } from './types';

function Products() {
  const {
    productsStore: { isAbleToLoadSCV, uploadSCV },
    popupStore: { popupName, openPopup, closePopup },
    mainStore: { selectedCountryCode },
    alertStore: { showAlert },
  } = useStore();

  const [uploadError, setUploadError] = useState<string | null>(null);

  const { brandId } = useParams();

  const { t } = useMyTranslations<ProductsTransFields>([productsTranslations.name]);

  const navigate = useNavigate();

  const { isFetching, text } = useGetBrandData();

  const { mutate, isPending } = useMutation({
    mutationFn: uploadSCV,
    mutationKey: ['uploadSCV'],
    onSuccess: () => {
      closePopup();
      showAlert(t('uploadSuccess'), 'success');
    },
    onError: (err) => {
      setUploadError(err.message);
    },
  });

  const useFormMethods = useForm<{ search: string }>();
  const { watch } = useFormMethods;

  const defaultValues = useMemo((): ProductUploadCSVForm | null => {
    return brandId && selectedCountryCode
      ? { brandId, currencyCode: getCurrencyCodeFromCountry(selectedCountryCode.countryCode) }
      : null;
  }, [brandId, selectedCountryCode]);

  return (
    <div className="page-container">
      <PageHeader text={text} />
      <BrandTabs currentTab="product list" />
      <div className="flex flex-row justify-between items-center px-[48px] sm-page-padding gap-x-[24px] max-md:gap-x-[12px]">
        <FormProvider {...useFormMethods}>
          <Input name="search" type="text" placeholder={t('search')} />
        </FormProvider>
        <AddEntityButton
          text={t('uploadSCV')}
          onClick={() => openPopup('uploadCSV')}
          icon={<ImportIcon width={20} height={20} className="md:hidden" />}
          disabled={!isAbleToLoadSCV}
        />
        <AddEntityButton
          text={t('addProduct')}
          onClick={() => navigate(`/brands/${brandId}/add-product`)}
        />
      </div>
      {isFetching ? <AppLoader /> : <ProductsTable search={watch('search')} />}
      <Popup onClose={() => setUploadError(null)}>
        {popupName === 'uploadCSV' && defaultValues && (
          <UploadCSVPopup
            defaultValues={defaultValues}
            onSubmit={(data) => mutate(data)}
            error={uploadError}
            disabled={isPending}
          />
        )}
      </Popup>
    </div>
  );
}

export default observer(Products);
